// /owner/program — the program list (slice 4a-3; multi-program since
// SLICE-PASS-TYPES; membership since SLICE-MEMBERSHIP-PASS).
//
// Lists the tenant's programs (loyalty + membership) and lets the owner add
// another (gated by the admin's per-type cap, surfaced as a "X of Y used" seat
// line + a disabled Add button when all available types are at cap). "Add
// Program" opens a pass-type picker — loyalty is always selectable; membership
// is selectable when the tenant's membership cap has a free seat; event ticket
// is shown as "coming soon". Each program links into the builder
// (/owner/program/:id/builder):
//   draft  → "Continue setup"
//   active → "Edit appearance" (cosmetic-only)

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import type { EventTicketConfig, MembershipConfig } from '@naya/shared'

import { OwnerApiError } from '../ownerApi.js'
import {
  useCreateProgram,
  useMakeDefaultProgram,
  usePrograms,
  useSetStaffCanRecoverPass,
  useSetStaffEnrollable,
} from '../program/hooks/useProgram.js'
import type { PassType } from '../program/types.js'
import '../program/programBuilder.css'

type PassTypeOption = {
  value: 'loyalty' | 'membership' | 'event_ticket'
  label: string
  description: string
  enabled: boolean
}

export function ProgramPage() {
  const navigate = useNavigate()
  const { data, isLoading, isError, refetch } = usePrograms()
  const create = useCreateProgram()
  const makeDefault = useMakeDefaultProgram()
  const setStaffEnrollable = useSetStaffEnrollable()
  const setStaffCanRecoverPass = useSetStaffCanRecoverPass()
  const [pickerOpen, setPickerOpen] = useState(false)

  async function addProgram(passType: PassType) {
    try {
      const res = await create.mutateAsync(passType)
      navigate(`/owner/program/${res.program.id}/builder`)
    } catch (err) {
      toast.error(
        err instanceof OwnerApiError
          ? err.userMessage
          : 'Could not create the program.',
      )
    }
  }

  if (isLoading) {
    return <p className="owner-loading">Loading…</p>
  }

  if (isError) {
    return (
      <div className="owner-card">
        <div className="owner-card-title">Couldn't load your programs</div>
        <p className="owner-card-sub">Something went wrong fetching them.</p>
        <button
          type="button"
          className="owner-btn"
          onClick={() => void refetch()}
        >
          Retry
        </button>
      </div>
    )
  }

  const programs = data?.programs ?? []
  // Archived programs are an admin/DB-only state with no owner action — hide
  // them from the list.
  const visible = programs.filter((p) => p.status !== 'archived')
  const multi = visible.length > 1

  // Per-type seat usage (cap null = unlimited, 0 = type not in this plan).
  // `used` counts any-status programs of that type — matches the create gate.
  const loyaltyCap = data?.loyalty_cap ?? null
  const loyaltyUsed = data?.loyalty_used ?? 0
  const loyaltyAtCap = loyaltyCap !== null && loyaltyUsed >= loyaltyCap

  const membershipCap = data?.membership_cap ?? null
  const membershipUsed = data?.membership_used ?? 0
  // Membership is offered when the plan includes it (cap > 0 or null=unlimited)
  // AND there's a free seat. A 0 cap means the plan doesn't include memberships.
  const membershipInPlan = membershipCap === null || membershipCap > 0
  const membershipAvailable =
    membershipInPlan &&
    (membershipCap === null || membershipUsed < membershipCap)

  // SLICE-EVENT-TICKET — same gating shape as membership.
  const eventCap = data?.event_ticket_cap ?? null
  const eventUsed = data?.event_ticket_used ?? 0
  const eventInPlan = eventCap === null || eventCap > 0
  const eventAvailable =
    eventInPlan && (eventCap === null || eventUsed < eventCap)

  // The "Add" button is disabled only when no type has a free seat.
  const canAddAny = !loyaltyAtCap || membershipAvailable || eventAvailable

  const seatLine =
    loyaltyCap !== null
      ? `Loyalty programs: ${loyaltyUsed} of ${loyaltyCap} used`
      : `Loyalty programs: ${loyaltyUsed} active`
  const seatHint = !canAddAny
    ? "You've reached the number of programs included in your plan. Contact Naya to add more."
    : membershipInPlan
      ? 'Add a loyalty stamp card or a membership.'
      : 'Add another to run more than one card.'

  return (
    <div>
      <h1>Programs</h1>
      <p className="owner-greeting-sub">
        The passes your customers can keep in their wallet. Add another to run
        more than one.
      </p>

      <div className="owner-card">
        <div className="owner-card-title">{seatLine}</div>
        {membershipInPlan && (
          <div className="owner-card-title">
            {membershipCap !== null
              ? `Memberships: ${membershipUsed} of ${membershipCap} used`
              : `Memberships: ${membershipUsed} active`}
          </div>
        )}
        <p className="owner-card-sub">{seatHint}</p>
        <button
          type="button"
          className="owner-btn owner-btn-primary"
          onClick={() => setPickerOpen(true)}
          disabled={!canAddAny || create.isPending}
        >
          {create.isPending ? 'Creating…' : 'Add Program'}
        </button>
      </div>

      {pickerOpen && (
        <ProgramTypeModal
          loyaltyAvailable={!loyaltyAtCap}
          membershipAvailable={membershipAvailable}
          membershipInPlan={membershipInPlan}
          eventAvailable={eventAvailable}
          eventInPlan={eventInPlan}
          onCancel={() => setPickerOpen(false)}
          creating={create.isPending}
          onCreate={(passType) => {
            setPickerOpen(false)
            void addProgram(passType)
          }}
        />
      )}

      {visible.length === 0 ? (
        <div className="owner-card">
          <p className="owner-card-sub">
            No programs yet. Add your first one above — it takes about five
            minutes to set up the design and details.
          </p>
        </div>
      ) : (
        <div className="program-list">
          {visible.map((p) => {
            const isActive = p.status === 'active'
            const isMembership = p.pass_type === 'membership'
            const isEvent = p.pass_type === 'event_ticket'
            const mCfg = isMembership
              ? (p.type_config as MembershipConfig | null)
              : null
            const eCfg = isEvent
              ? (p.type_config as EventTicketConfig | null)
              : null
            // Only surface default/switch affordances when there's a real choice.
            const showDefault = multi && isActive && !isEvent
            return (
              <div key={p.id} className="owner-card program-list-card">
                <div className="program-active-head">
                  <div className="owner-card-title">{p.name}</div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {/* SLICE-MEMBERSHIP-PASS / SLICE-EVENT-TICKET — type badge. */}
                    <span className="program-type-badge">
                      {isEvent
                        ? 'Event ticket'
                        : isMembership
                          ? 'Membership'
                          : 'Loyalty'}
                    </span>
                    {showDefault && p.is_default && (
                      <span className="program-status-pill is-active">
                        Default
                      </span>
                    )}
                    <span
                      className={`program-status-pill${
                        isActive ? ' is-active' : ''
                      }`}
                    >
                      {isActive ? 'Active' : 'Draft'}
                    </span>
                  </div>
                </div>
                <p className="owner-card-sub">
                  {isEvent
                    ? eCfg?.event_name?.trim()
                      ? eCfg.event_name.trim()
                      : 'Event ticket'
                    : isMembership
                      ? mCfg?.tiers_enabled && (mCfg.tiers?.length ?? 0) > 0
                        ? `${mCfg.tiers.length} tiers`
                        : 'Membership'
                      : p.total_stamps != null
                        ? `${p.total_stamps} stamps`
                        : 'Stamp card'}
                  {isActive
                    ? isEvent
                      ? ' · issuing tickets'
                      : isMembership
                        ? ' · members can be issued'
                        : ' · customers can enroll'
                    : ' · not live yet'}
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {isEvent && isActive && (
                    <button
                      type="button"
                      className="owner-btn owner-btn-primary"
                      onClick={() => navigate(`/owner/events/${p.id}`)}
                    >
                      Manage event
                    </button>
                  )}
                  <button
                    type="button"
                    className={
                      isEvent && isActive
                        ? 'owner-btn'
                        : 'owner-btn owner-btn-primary'
                    }
                    onClick={() => navigate(`/owner/program/${p.id}/builder`)}
                  >
                    {isActive ? 'Edit appearance' : 'Continue setup'}
                  </button>
                  {showDefault && !p.is_default && (
                    <button
                      type="button"
                      className="owner-btn"
                      disabled={makeDefault.isPending}
                      onClick={() =>
                        makeDefault.mutate(p.id, {
                          onSuccess: () =>
                            toast.success(`"${p.name}" is now the default`),
                          onError: (err) =>
                            toast.error(
                              err instanceof OwnerApiError
                                ? err.userMessage
                                : 'Could not set the default.',
                            ),
                        })
                      }
                    >
                      Make default
                    </button>
                  )}
                </div>

                {/* Owner control — let staff issue this program from
                    /staff/enroll. Only meaningful once the program is active
                    (a draft isn't enrolling yet). Membership defaults OFF so the
                    paid "take payment, then hand off" issuance stays owner-only
                    until the owner opts in. */}
                {isActive && (
                  <div className="program-staff-enroll">
                    <label className="program-toggle">
                      <input
                        type="checkbox"
                        checked={p.staff_enrollable}
                        disabled={setStaffEnrollable.isPending}
                        onChange={(e) =>
                          setStaffEnrollable.mutate(
                            { id: p.id, value: e.target.checked },
                            {
                              onSuccess: () =>
                                toast.success(
                                  e.target.checked
                                    ? `Staff can now enroll into "${p.name}"`
                                    : `Staff can no longer enroll into "${p.name}"`,
                                ),
                              onError: (err) =>
                                toast.error(
                                  err instanceof OwnerApiError
                                    ? err.userMessage
                                    : 'Could not update the setting.',
                                ),
                            },
                          )
                        }
                      />
                      <span className="program-field-label">
                        Staff can enroll customers
                      </span>
                    </label>
                    <span className="program-field-hint">
                      {isMembership
                        ? 'When on, your staff can issue this membership from the staff app. Off by default so paid memberships stay owner-only.'
                        : 'When on, your staff can enroll customers into this card from the staff app.'}
                    </span>

                    {/* Staff pass recovery — off by default. Shows "Recover
                        pass" on this program's cards in the staff app's
                        customer search. Owners can always recover from
                        Customers. */}
                    <label className="program-toggle program-staff-recover">
                      <input
                        type="checkbox"
                        checked={p.staff_can_recover_pass}
                        disabled={setStaffCanRecoverPass.isPending}
                        onChange={(e) =>
                          setStaffCanRecoverPass.mutate(
                            { id: p.id, value: e.target.checked },
                            {
                              onSuccess: () =>
                                toast.success(
                                  e.target.checked
                                    ? `Staff can now recover "${p.name}" passes`
                                    : `Staff can no longer recover "${p.name}" passes`,
                                ),
                              onError: (err) =>
                                toast.error(
                                  err instanceof OwnerApiError
                                    ? err.userMessage
                                    : 'Could not update the setting.',
                                ),
                            },
                          )
                        }
                      />
                      <span className="program-field-label">
                        Staff can recover passes
                      </span>
                    </label>
                    <span className="program-field-hint">
                      When on, your staff can resend a customer's pass link
                      from the staff app's customer search, for customers who
                      lost or never added their pass.
                    </span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/**
 * Pass-type picker shown when adding a program. Loyalty is always selectable;
 * membership is selectable when the tenant's plan includes it and a seat is
 * free (else it's shown disabled — "in your plan but full", or "coming soon"
 * when the plan doesn't include it at all). Event ticket is always "coming
 * soon" in V1. Create mints a draft of the selected type.
 */
function ProgramTypeModal({
  loyaltyAvailable,
  membershipAvailable,
  membershipInPlan,
  eventAvailable,
  eventInPlan,
  onCancel,
  onCreate,
  creating,
}: {
  loyaltyAvailable: boolean
  membershipAvailable: boolean
  /** Whether the plan includes memberships at all (cap > 0 or unlimited). */
  membershipInPlan: boolean
  eventAvailable: boolean
  /** Whether the plan includes event tickets at all. */
  eventInPlan: boolean
  onCancel: () => void
  onCreate: (passType: PassType) => void
  creating: boolean
}) {
  const options: Array<PassTypeOption & { soonLabel?: string | undefined }> = [
    {
      value: 'loyalty',
      label: 'Loyalty stamp card',
      description: 'Customers collect stamps toward rewards.',
      enabled: loyaltyAvailable,
    },
    {
      value: 'membership',
      label: 'Membership',
      description: 'Recurring members with a tier, number, and benefits.',
      enabled: membershipAvailable,
      // Distinguish "not in your plan" from "you've used all your seats".
      soonLabel: membershipInPlan ? undefined : 'Not in your plan',
    },
    {
      value: 'event_ticket',
      label: 'Event ticket',
      description: 'One-time passes for an event: tiers, capacity, gate scan.',
      enabled: eventAvailable,
      soonLabel: eventInPlan ? undefined : 'Not in your plan',
    },
  ]

  // Default the selection to the first enabled option.
  const firstEnabled = options.find((o) => o.enabled)?.value ?? 'loyalty'
  const [selected, setSelected] = useState<PassTypeOption['value']>(firstEnabled)
  const canCreate =
    (selected === 'loyalty' && loyaltyAvailable) ||
    (selected === 'membership' && membershipAvailable) ||
    (selected === 'event_ticket' && eventAvailable)

  return (
    <div
      className="stamp-modal-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
    >
      <div className="stamp-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="stamp-modal-title">What kind of program?</h2>
        <p className="owner-card-sub">
          Pick the kind of pass your customers will keep in their wallet.
        </p>
        <div className="program-type-options">
          {options.map((opt) => (
            <label
              key={opt.value}
              className={`program-type-option${
                selected === opt.value ? ' is-selected' : ''
              }${opt.enabled ? '' : ' is-disabled'}`}
            >
              <input
                type="radio"
                name="program-type"
                value={opt.value}
                checked={selected === opt.value}
                disabled={!opt.enabled}
                onChange={() => opt.enabled && setSelected(opt.value)}
              />
              <span className="program-type-option-body">
                <span className="program-type-option-label">
                  {opt.label}
                  {!opt.enabled && opt.soonLabel && (
                    <span className="program-type-soon">{opt.soonLabel}</span>
                  )}
                </span>
                <span className="program-type-option-desc">
                  {opt.description}
                </span>
              </span>
            </label>
          ))}
        </div>
        <div className="owner-form-actions" style={{ marginTop: 16 }}>
          <button
            type="button"
            className="owner-btn"
            onClick={onCancel}
            disabled={creating}
          >
            Cancel
          </button>
          <button
            type="button"
            className="owner-btn owner-btn-primary"
            onClick={() => onCreate(selected as PassType)}
            disabled={creating || !canCreate}
          >
            {creating ? 'Creating…' : 'Create program'}
          </button>
        </div>
      </div>
    </div>
  )
}
